import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { FieldError } from '@/components/ui/fieldError';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { Textarea } from '@/components/ui/textarea';
import { ToggleSwitch } from '@/components/ui/toggleSwitch';
import {
  isPreVisitChoiceQuestionType,
  type PreVisitQuestionType,
} from '@/constants/preVisitQuestionTypes';
import { cn } from '@/lib/utils';
import { isBlank } from '@/utils/formValidation';
import { showErrorNotification } from '@/utils/notifications';
import { PreVisitQuestionOptionsEditor } from './PreVisitQuestionOptionsEditor';
import { PreVisitQuestionTypePicker } from './PreVisitQuestionTypePicker';
import {
  buildNewOptionRow,
  isQuestionRowValid,
  type EditableOptionRow,
  type EditableQuestionRow,
} from './preVisitFormEditorState';

type EditorLanguage = 'en' | 'pl';

interface PreVisitQuestionDialogProps {
  initialRow: EditableQuestionRow;
  isNew: boolean;
  onSave: (row: EditableQuestionRow) => void;
  onCancel: () => void;
}

export function PreVisitQuestionDialog({
  initialRow,
  isNew,
  onSave,
  onCancel,
}: PreVisitQuestionDialogProps) {
  const { t } = useTranslation('consultations');
  const [row, setRow] = useState<EditableQuestionRow>(initialRow);
  const [activeLanguage, setActiveLanguage] = useState<EditorLanguage>('en');
  const [showValidation, setShowValidation] = useState(false);

  function changeQuestionType(questionType: PreVisitQuestionType) {
    setRow(function apply(current) {
      const shouldSeedOptions =
        isPreVisitChoiceQuestionType(questionType) && current.options.length === 0;
      const nextOptions: EditableOptionRow[] = shouldSeedOptions
        ? [buildNewOptionRow(), buildNewOptionRow()]
        : current.options;
      return { ...current, questionType, options: nextOptions };
    });
  }

  function save() {
    if (isQuestionRowValid(row) === false) {
      setShowValidation(true);
      showErrorNotification(t('preVisitEditor.invalidQuestion'));
      return;
    }
    onSave(row);
  }

  return (
    <ModalOverlay onRequestClose={onCancel} ariaLabel={t('preVisitEditor.dialogTitle')}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-zoowhite border border-zooborder rounded-3xl shadow-2xl p-6">
        <h2 className="text-lg font-bold text-zooindigo mb-4">
          {isNew ? t('preVisitEditor.addTitle') : t('preVisitEditor.editTitle')}
        </h2>

        <div className="space-y-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-zoomuted mb-2">
              {t('preVisitEditor.typeLabel')}
            </p>
            <PreVisitQuestionTypePicker value={row.questionType} onChange={changeQuestionType} />
          </div>

          <div className="inline-flex bg-zoooffwhite border border-zooborder rounded-lg p-1">
            {(['en', 'pl'] as EditorLanguage[]).map(function renderLanguageTab(language) {
              return (
                <button
                  key={language}
                  type="button"
                  onClick={function select() {
                    setActiveLanguage(language);
                  }}
                  className={cn(
                    'px-3 py-1 rounded-md text-xs font-bold transition-colors',
                    activeLanguage === language ? 'bg-zooprimary text-zoowhite' : 'text-zoomuted'
                  )}
                >
                  {language === 'en' ? t('preVisitEditor.english') : t('preVisitEditor.polish')}
                </button>
              );
            })}
          </div>

          <div className={cn('space-y-3', activeLanguage === 'en' ? '' : 'hidden')}>
            <div className="space-y-1.5">
              <Label htmlFor="pre-visit-question-label-en">{t('preVisitEditor.labelEn')}</Label>
              <Input
                id="pre-visit-question-label-en"
                value={row.labelEn}
                hasError={showValidation && isBlank(row.labelEn)}
                onChange={function update(changeEvent) {
                  setRow(function apply(current) {
                    return { ...current, labelEn: changeEvent.target.value };
                  });
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pre-visit-question-help-en">{t('preVisitEditor.helpEn')}</Label>
              <Textarea
                id="pre-visit-question-help-en"
                rows={2}
                value={row.helpEn}
                onChange={function update(changeEvent) {
                  setRow(function apply(current) {
                    return { ...current, helpEn: changeEvent.target.value };
                  });
                }}
              />
            </div>
          </div>

          <div className={cn('space-y-3', activeLanguage === 'pl' ? '' : 'hidden')}>
            <div className="space-y-1.5">
              <Label htmlFor="pre-visit-question-label-pl">{t('preVisitEditor.labelPl')}</Label>
              <Input
                id="pre-visit-question-label-pl"
                value={row.labelPl}
                hasError={showValidation && isBlank(row.labelPl)}
                onChange={function update(changeEvent) {
                  setRow(function apply(current) {
                    return { ...current, labelPl: changeEvent.target.value };
                  });
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pre-visit-question-help-pl">{t('preVisitEditor.helpPl')}</Label>
              <Textarea
                id="pre-visit-question-help-pl"
                rows={2}
                value={row.helpPl}
                onChange={function update(changeEvent) {
                  setRow(function apply(current) {
                    return { ...current, helpPl: changeEvent.target.value };
                  });
                }}
              />
            </div>
          </div>

          {isPreVisitChoiceQuestionType(row.questionType) ? (
            <PreVisitQuestionOptionsEditor
              options={row.options}
              showValidation={showValidation}
              onChange={function changeOptions(options) {
                setRow(function apply(current) {
                  return { ...current, options };
                });
              }}
            />
          ) : null}

          <div className="flex items-center justify-between gap-3 pt-1">
            <span className="text-sm font-medium text-zooindigo">
              {t('preVisitEditor.requiredLabel')}
            </span>
            <ToggleSwitch
              isChecked={row.isRequired}
              accessibleLabel={t('preVisitEditor.requiredLabel')}
              onToggle={function toggle() {
                setRow(function apply(current) {
                  return { ...current, isRequired: current.isRequired === false };
                });
              }}
            />
          </div>

          {showValidation ? (
            <FieldError messageKey="consultations:preVisitEditor.invalidQuestion" />
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-3 mt-6">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('preVisitEditor.cancel')}
          </Button>
          <Button type="button" onClick={save}>
            {t('preVisitEditor.saveQuestion')}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}
